import React, { useCallback, memo } from 'react';
import styled from 'styled-components';

import Button from '../../../Button';
import { Types as ButtonTypes } from '../../../Button/Button';
import Modal from '../../../Modal';
import SuccessIcon from '../../../SuccessIcon';

import { Attention as AttentionIcon } from '@icons/common';
import { ValueOf } from '@utils/tsUtils';

export const TYPES = {
  FAILURE: 'failure',
  SUCCESS: 'success',
  WARNING: 'warning',
};

const DEFAULT_SUBMIT = 'Продолжить';

interface IActionPopup {
  className?: string;
  description: string | null;
  isOpen: boolean;
  title: string | null;
  type?: ValueOf<typeof TYPES>;
  submitText?: string;

  onCancel: () => void;
  onSubmit: () => void;
}

const ActionPopup = ({
  isOpen,
  title = null,
  type = TYPES.FAILURE,
  submitText = DEFAULT_SUBMIT,
  description = null,
  onCancel,
  onSubmit,
  className,
}: IActionPopup) => {
  const handleCancel = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();

      onCancel();
    },
    [onCancel]
  );

  const handleSubmit = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();

      onSubmit();
    },
    [onSubmit]
  );

  return (
    <StyledPopup
      className={`action-popup ${className}`}
      isOpen={isOpen}
      root={document.getElementById('action-modals')}
    >
      <StyledForm onSubmit={handleSubmit}>
        {type === TYPES.WARNING ? (
          <AttentionIcon width={60} />
        ) : (
          <SuccessIcon success={type === TYPES.SUCCESS} />
        )}
        {title && <StyledTitle>{title}</StyledTitle>}
        {description && <StyledDescription>{description}</StyledDescription>}
        <StyledControls>
          <StyledButton type={ButtonTypes.SUBMIT}>{submitText}</StyledButton>
          <StyledCancelButton onClick={handleCancel}>Отмена</StyledCancelButton>
        </StyledControls>
      </StyledForm>
    </StyledPopup>
  );
};

export const StyledPopup = styled(Modal)`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
`;

const StyledForm = styled.form`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 40px 10px 10px 10px;
  background: white;
  border-radius: 6px;
  width: 416px;
`;

const StyledTitle = styled.p`
  font-size: 18px;
  font-weight: bold;
  line-height: 22px;
  color: #17181a;
  margin-bottom: 10px;
  margin-top: 30px;
`;

const StyledDescription = styled.div`
  width: 250px;
  margin-bottom: 25px;
  font-family: 'Backpack', sans-serif;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
`;

const StyledControls = styled.div`
  display: flex;
  width: 100%;
`;

export const StyledButton = styled(Button)`
  width: 50%;
  border-radius: 4px;
  color: white;
  background-color: #007aff;
  font-size: 14px;
  height: 42px;
  line-height: 24px;
  min-height: 25px;
  min-width: 25px;
  padding: 0;
  position: relative;
  text-align: center;
`;

export const StyledCancelButton = styled(Button)`
  border: none;
  width: 50%;
  border-radius: 4px;
  color: #4d4d4d;
  background-color: transparent;
  font-weight: normal;
  font-size: 14px;
  height: 42px;
  line-height: 24px;
  min-height: 25px;
  min-width: 25px;
  padding: 0;
  position: relative;
  text-align: center;
`;

export default memo(ActionPopup);
